import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useProducts } from '../../context/ProductsContext';
import { useMedia } from '../../context/MediaContext';
import { pickProductMediaUrl } from '../../utils/media';
import { pickVariantImageUrl } from '../../utils/media';
import { useCopy } from '../../hooks/useCopy';
import { useTranslation } from 'react-i18next';
import { useLocale } from '../../context/LocaleContext';

const categoriesDef = [
  {
    nameKey: "footer.earringsHoops",
    descKey: "home-categories-earrings-and-hoops-desc",
    descFallback: "Explore our unique collection of earrings and hoops.",
    filterName: "Earrings",
    slug: "earrings-and-hoops"
  },
  {
    nameKey: "footer.rings",
    descKey: "home-categories-rings-desc",
    descFallback: "Discover our exquisite range of rings.",
    filterName: "Rings",
    slug: "rings"
  },
  {
    nameKey: "footer.uniqueFinds",
    descKey: "home-categories-unique-finds-desc",
    descFallback: "Find one-of-a-kind pieces in our unique finds.",
    filterName: "Pendant",
    slug: "unique-finds"
  }
];

const CategoriesSection: React.FC = () => {
  const { t } = useTranslation();
  const { localePath } = useLocale();
  const { text } = useCopy('home');
  const { products } = useProducts();
  const { getMediaByProduct, loading: mediaLoading } = useMedia();
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [categoryImages, setCategoryImages] = useState<string[][]>([]);

  // Helper to get product main image from Media table with fallbacks
  const getProductMainImage = (product: any): string => {
    // Don't query media until it's loaded
    if (!mediaLoading) {
        let media = product.airtableId ? getMediaByProduct(product.airtableId) : [];
        if (media.length === 0) media = getMediaByProduct(product.id);
        if (media.length === 0) media = getMediaByProduct(product.slug);

        if (media.length > 0) {
          const mainImage = pickProductMediaUrl(media, ['-main', ' main', 'main-', '_main', '-primary', 'primary', 'front', 'principal'], 'product-main');
          if (mainImage) return mainImage;
        }
    }

    const variantImage = pickVariantImageUrl(product.variants?.[0]?.images, 0);
    if (variantImage) return variantImage;

    return '/placeholder-image.jpg';
  };

  useEffect(() => {
    // Get and store images once when component mounts
    const getInitialImages = () => {
      if (!products || products.length === 0) return;

      const allImages = categoriesDef.map(category => {
        let categoryProducts;
        
        if (category.filterName === "Earrings") {
          categoryProducts = products.filter(p => 
            ["Earrings", "Hoops", "Earring (Singular)", "Hoop (Singular)"].includes(p.category)
          );
        } else if (category.filterName === "Rings") {
          categoryProducts = products.filter(p => p.category === category.filterName);
        } else {
          categoryProducts = products.filter(p => 
            !["Earrings", "Hoops", "Earring (Singular)", "Hoop (Singular)", "Rings"].includes(p.category)
          );
        }

        const shuffled = [...categoryProducts].sort(() => 0.5 - Math.random());
        const images = shuffled.slice(0, 3)
          .map(p => getProductMainImage(p))
          .filter((img): img is string => !!img);
        while (images.length < 3) {
          images.push(images[images.length - 1] || '/placeholder-image.jpg');
        }
        return images;
      });
      setCategoryImages(allImages);
    };

    getInitialImages();
  }, [products, mediaLoading]);

  return (
    <section className="py-16 md:py-24">
      <div className="text-center mb-12">
        <h2 className="text-3xl md:text-4xl font-bold text-black dark:text-white mb-4">
          {text('home-categories-title', 'Find Something Special')}
        </h2>
        <p className="text-lg text-black/80 dark:text-white/80">
          {text('home-categories-subtitle', 'Discover pieces that speak to your style')}
        </p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-0 w-full">
        {categoriesDef.map((category, index) => (
          <motion.div
            key={index}
            whileHover={{ scale: 1.02 }}
            transition={{ duration: 0.3 }}
          >
            <Link
              to={localePath('/categories/:category', { category: category.slug })}
              className="relative block"
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              <div className="h-80 md:h-[500px] w-full overflow-hidden relative">
                {categoryImages[index]?.map((image, imgIndex) => (
                  <img
                    key={imgIndex}
                    src={image}
                    alt={t(category.nameKey)}
                    className={`w-full h-full object-cover absolute top-0 left-0 transition-opacity duration-500 ${
                      hoveredIndex === index && imgIndex !== 0 ? 'opacity-0' : 'opacity-100'
                    }`}
                    style={{
                      transitionDelay: hoveredIndex === index ? `${imgIndex * 200}ms` : '0ms'
                    }}
                  />
                ))}
              </div>
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-b from-transparent via-black/40 to-black/70 pt-20 pb-8 px-4 text-center text-white">
                <h3 className="text-2xl font-bold">{t(category.nameKey)}</h3>
                <p className="mt-2">{text(category.descKey, category.descFallback)}</p>
              </div>
            </Link>
          </motion.div>
        ))}
      </div>
    </section>
  );
};

export default CategoriesSection;